import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Avatar, RoleBadge, Wordmark } from '../components/ui';

// Primary tools (top-level nav) — shown only when entitled. glyph = compact icon.
const TOOL_LINKS = [
  { key: 'lead_finder', to: '/tools/lead-finder', label: 'Lead Score', glyph: '◆' },
  { key: 'email', to: '/tools/email', label: 'Email', glyph: '✉' },
  { key: 'caller', to: '/tools/caller', label: 'Calls', glyph: '☎' },
  { key: 'walkin', to: '/tools/walkin', label: 'Walk-In', glyph: '⚑' },
  { key: 'content', to: '/tools/content', label: 'Content', glyph: '▦' },
];
// The home base (Phase 10: Dashboard retired). Contacts is where a rep lives;
// Tasks is their to-dos. Stage/pipeline tracking now lives inline on Contacts,
// with the board reachable as a secondary "Board view" from there.
const BASE_LINKS = [
  { to: '/contacts', label: 'Contacts', glyph: '▤' },
  { to: '/tasks', label: 'Tasks', glyph: '✓' },
];

export default function AppShell() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [acctOpen, setAcctOpen] = useState(false);
  if (!me) return null;

  const owned = new Set(me.entitlements || []);
  const tools = TOOL_LINKS.filter((t) => owned.has(t.key));

  const MOBILE_NAV = [
    { to: '/contacts', label: 'Contacts', end: false },
    { to: '/tasks', label: 'Tasks', end: false },
    { to: '/', label: 'Tools', end: true },
    { to: '/usage', label: 'Usage', end: false },
    { to: '/settings', label: 'Settings', end: false },
  ];

  const primaryCls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-xl px-2.5 py-2.5 font-head text-[15px] font-semibold transition ${
      isActive ? 'bg-power/15 text-electric' : 'text-soft/80 hover:bg-surface-3 hover:text-soft'
    }`;
  const IconBox = ({ active, children }: { active?: boolean; children: React.ReactNode }) => (
    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-sm ${active ? 'bg-power/25 text-electric' : 'bg-surface-3 text-muted'}`}>{children}</span>
  );

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-surface/60 p-3 md:flex">
        <div className="px-2 py-2">
          <Wordmark />
        </div>

        {/* Account dropdown (accordion — opening pushes the nav down) */}
        <div className="mt-2">
          <button
            onClick={() => setAcctOpen((v) => !v)}
            className="flex w-full items-center gap-3 rounded-xl border border-line bg-surface-2 px-3 py-2.5 text-left transition hover:border-line-strong"
          >
            <Avatar name={me.user.fullName} email={me.user.email} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-head text-sm font-semibold text-soft">{me.group?.name}</div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                <RoleBadge role={me.role} />
              </div>
            </div>
            <span className={`text-muted transition-transform ${acctOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {acctOpen && (
            <div className="mt-1 space-y-0.5 rounded-xl border border-line bg-surface-2/60 p-1">
              <AcctLink to="/team" label="Team" />
              <AcctLink to="/usage" label="Usage" />
              <AcctLink to="/settings" label="Settings" />
              {me.isAdmin && <AcctLink to="/admin" label="Admin" />}
              <button
                onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-muted transition hover:bg-surface-3 hover:text-soft"
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Primary nav */}
        <nav className="mt-3 flex-1 space-y-1 overflow-y-auto">
          <NavLink to="/" end className="mb-1 flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm font-semibold text-muted transition hover:bg-surface-3 hover:text-soft">
            <IconBox>⌂</IconBox> All tools
          </NavLink>

          {/* Home base: Contacts + Tasks (Phase 10 — Dashboard retired). */}
          {BASE_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={primaryCls}>
              {({ isActive }) => (<><IconBox active={isActive}>{l.glyph}</IconBox> {l.label}</>)}
            </NavLink>
          ))}

          <div className="my-2 border-t border-line" />

          {tools.map((t) => (
            <NavLink key={t.to} to={t.to} className={primaryCls}>
              {({ isActive }) => (<><IconBox active={isActive}>{t.glyph}</IconBox> {t.label}</>)}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-line px-4 py-3 md:hidden">
          <Wordmark />
          <RoleBadge role={me.role} />
        </header>
        <main className="flex-1 overflow-y-auto px-5 py-6 sm:px-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
        <nav className="grid grid-cols-5 border-t border-line bg-surface/80 md:hidden">
          {MOBILE_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `py-2 text-center text-[11px] font-semibold ${isActive ? 'text-electric' : 'text-muted'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function AcctLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} className={({ isActive }) => `block rounded-lg px-3 py-2 text-sm font-semibold transition ${isActive ? 'bg-power/15 text-electric' : 'text-soft/80 hover:bg-surface-3 hover:text-soft'}`}>
      {label}
    </NavLink>
  );
}
